import { AllyEventListenerInstance, AllyObserverInstance } from "../classes";

/**
 * Contains Tools for the interaction with the De classes.
 */
export class AllyTools {
  /**
   * A dictionary of all active event listeners and mutation observers.
   */
  private static dict: AllyTools["dict"] = {};
  private static pauses = 0;
  public dict: {[key:string]: (AllyEventListenerInstance|AllyObserverInstance)[]};

  constructor() {
    this.dict = AllyTools.dict;
  }

  /**
   * Creates a record of the provided listener.
   * @param id The id of the node the listener is listening on.
   * @param listener The listener to be provided.
   * @returns A callback to remove the record.
   */
  public set(id: string, listener: AllyEventListenerInstance|AllyObserverInstance) {
    if (this.dict[id] === undefined) {
      this.dict[id] = [];
    }
    this.dict[id].push(listener);
    return () => {
      if (this.dict[id].length > 0) {
        let index = this.dict[id].indexOf(listener);
        if (index !== -1) {
          this.dict[id].splice(index, 1);
        }
      }
      if (this.dict[id].length <= 0) {
        delete this.dict[id];
      }
    }
  }
  /**
   * Returns a list of listeners listening on the node that has the provided id.
   * @param id The id to be provided.
   * @returns The list of listeners or null if no node was found.
   */
  public getListenersById(id: string) {
    let result = this.dict[id] as (AllyEventListenerInstance | AllyObserverInstance)[]|null;
    if (result === undefined) {
      result = null;
    }
    return result;
  }
  /**
   * Events triggered by the provided callback will be ignored by all listeners of the Ally classes.
   * @param callback The callback to be provided.
   */
  public runOutside(callback: () => void) {
    // One flat pause: nesting every listener's runOutside grew the stack with the page size.
    const observers = Object.values(this.dict).flat().filter((listener): listener is AllyObserverInstance => "pause" in listener);
    AllyTools.pauses++;
    observers.forEach((observer) => observer.pause());
    try {
      callback();
    } finally {
      observers.forEach((observer) => observer.resume());
      AllyTools.pauses--;
    }
  }

  public static listenerCount(): number {
    return Object.values(AllyTools.dict).reduce((count, listeners) => count + listeners.length, 0);
  }

  /** True while runOutside runs; event listeners ignore events meanwhile. */
  public static get paused(): boolean {
    return AllyTools.pauses > 0;
  }
}
